import { BaseWebComponentView } from '../../../../framework/BaseWebComponentView';
import { GzButtonWebComponent } from '../../../../ui/gzButton';
import { TemplateState } from '../../../../types/common/types';
import { GzTextWebComponent } from '../../../../ui/gzText';
import { translateFn } from '../../../../utils/translator.helper';
import { GzTitleWebComponent } from '../../../../ui/gzTitle';
import { ReconfigureAgentTypeFormWebComponent } from '../../common/reconfigureAgentTypeForm';
import {
  WEB_COMPONENT_PACKAGE_MODULES_FORM,
  WEB_COMPONENT_PACKAGE_SCAN_MODES_FORM,
  WEB_COMPONENT_PACKAGE_SECURITY_SERVER_ASSIGNMENT_GRID,
  WEB_COMPONENT_RECONFIGURE_AGENT_SCHEDULER_FORM,
  WEB_COMPONENT_RECONFIGURE_AGENT_TYPE_FORM,
} from '../../../../webcomponents.constants';
import { PackageScanModesFormWebComponent } from '../../../../packages/common/packageScanModesForm';
import { toggleVisibility } from '../../../../utils/htmlElement.utils';
import { PackageActionContext } from '../../../../packages/common/types/PackageActionContext';
import { PackageSecurityServerAssignmentGridWebComponent } from '../../../../packages/common/packageSecurityServerAssignmentGrid';
import { PackageModulesFormWebComponent } from '../../../../packages/common/packageModulesForm';
import { ReconfigureAgentSchedulerFormWebComponent } from '../../common/reconfigureAgentShedulerForm';
import { MonthlyProductType } from '../../../../license/types/MonthlyProductType';
import { Feature } from '@nirvana/shared/constants';
import { ReconfigureAgentPanelEvents } from '../../common/events/ReconfigureAgentPanelEvents';
import { ReconfigureAgentViewData } from '../types';
import { CustomizableValidator } from '../../../../ui/gzForm/validators/customizable.validator';
import { GzInfoBoxWebComponent } from '../../../../ui/gzInfoBox';
import { ReconfigureAgentType } from '../../common/reconfigureAgentTypeForm/types';
import { LinkColor, LinkType } from '../../../../ui/gzLinkComponent/types';

export class ReconfigureAgentPanelView extends BaseWebComponentView {
  public dom: {
    $wrapper: HTMLElement;

    $reconfigureAgentDescription: GzTextWebComponent;
    $reconfigureAgentModulesSubtitle: GzTitleWebComponent;

    $reconfigureAgentModulesForm: PackageModulesFormWebComponent;

    $modulesRequirementsBoxInfo: GzInfoBoxWebComponent;

    $reconfigureAgentTypeForm: ReconfigureAgentTypeFormWebComponent;

    $reconfigureAgentSchedulerSubtitle: GzTitleWebComponent;
    $reconfigureAgentSchedulerForm: ReconfigureAgentSchedulerFormWebComponent;

    $reconfigureScanModesForm: PackageScanModesFormWebComponent;

    $reconfigureSecurityServerAssignmentGrid: PackageSecurityServerAssignmentGridWebComponent;

    $saveButton: GzButtonWebComponent;
    $cancelButton: GzButtonWebComponent;
  };

  getDomElements(): void {
    this.dom = {
      $wrapper: this.shadow.querySelector('.wrapper'),

      $reconfigureAgentDescription: this.shadow.querySelector('#reconfigure-agent-description'),
      $reconfigureAgentModulesSubtitle: this.shadow.querySelector('#reconfigure-agent-modules-subtitle'),

      $reconfigureAgentTypeForm: this.shadow.querySelector(WEB_COMPONENT_RECONFIGURE_AGENT_TYPE_FORM),
      $reconfigureAgentModulesForm: this.shadow.querySelector(WEB_COMPONENT_PACKAGE_MODULES_FORM),

      $modulesRequirementsBoxInfo: this.shadow.querySelector('#modules-requirements-box-info'),

      $reconfigureAgentSchedulerSubtitle: this.shadow.querySelector('#reconfigure-agent-scheduler-subtitle'),
      $reconfigureAgentSchedulerForm: this.shadow.querySelector(WEB_COMPONENT_RECONFIGURE_AGENT_SCHEDULER_FORM),

      $reconfigureScanModesForm: this.shadow.querySelector(WEB_COMPONENT_PACKAGE_SCAN_MODES_FORM),

      $reconfigureSecurityServerAssignmentGrid: this.shadow.querySelector(
        WEB_COMPONENT_PACKAGE_SECURITY_SERVER_ASSIGNMENT_GRID
      ),

      $saveButton: this.shadow.querySelector('#save-button'),
      $cancelButton: this.shadow.querySelector('#cancel-button'),
    };

    this.setLoadingState();
    this.getTranslations();
    this.setTranslatedStrings();
  }

  buildSuccessView(data: ReconfigureAgentViewData): void {
    this.dom.$reconfigureAgentTypeForm.injectDependencies(this.dependencies);

    this.dom.$reconfigureAgentModulesForm.formContext = PackageActionContext.Edit;
    this.dom.$reconfigureAgentModulesForm.useDefaultValues = false;
    this.dom.$reconfigureAgentModulesForm.setAvailableFeatures(data.availableFeatures);
    this.dom.$reconfigureAgentModulesForm.injectDependencies(this.dependencies);

    this.dom.$reconfigureScanModesForm.formContext = PackageActionContext.Edit;
    this.dom.$reconfigureScanModesForm.setContextFrat(false);
    this.dom.$reconfigureScanModesForm.setFeatures(data.availableFeatures);
    this.dom.$reconfigureScanModesForm.injectDependencies(this.dependencies);

    this.dom.$reconfigureSecurityServerAssignmentGrid.injectDependencies(this.dependencies);
    this.dom.$reconfigureSecurityServerAssignmentGrid.companyId = data.companyId;
    toggleVisibility(this.dom.$reconfigureSecurityServerAssignmentGrid, false);
    this.dom.$reconfigureSecurityServerAssignmentGrid.setVisibility(false);

    this.dom.$reconfigureAgentSchedulerForm.injectDependencies(this.dependencies);

    const startWebCompPromises: Promise<void>[] = [
      this.dom.$reconfigureAgentTypeForm.start(),
      this.dom.$reconfigureScanModesForm.start(),
      this.dom.$reconfigureSecurityServerAssignmentGrid.start(),
      this.dom.$reconfigureAgentModulesForm.start(),
      this.dom.$reconfigureAgentSchedulerForm.start(),
    ];

    Promise.all(startWebCompPromises)
      .then(() => {
        this.removeLoadingState();
        this.initScanModeSection(data.availableFeatures);
        this.initModulesSection();

        this.dom.$wrapper.dispatchEvent(
          new CustomEvent(ReconfigureAgentPanelEvents.OnViewInit, {
            bubbles: true,
            composed: true,
          })
        );
      })
      .catch(console.error);
  }

  getTranslations(): void {
    this.commonStrings = this.stringsTranslator.getCommonStrings();
  }

  setTranslatedStrings(): void {
    this.dom.$reconfigureAgentDescription.innerText = translateFn(
      'Choose the protection modules and roles you want on the target endpoints. You can add new modules besides the existing ones, remove certain modules, or match the modules installed with your selection.'
    );

    this.dom.$reconfigureAgentModulesSubtitle.innerText = translateFn('Modules');

    this.dom.$reconfigureAgentSchedulerSubtitle.innerText = translateFn('Scheduler');

    this.dom.$saveButton.innerText = this.commonStrings.save;
    this.dom.$cancelButton.innerText = translateFn('Cancel');
  }

  public setModulesRequirementsText(reconfigureType: ReconfigureAgentType): void {
    this.dom.$modulesRequirementsBoxInfo.innerHTML = '';

    if (reconfigureType === ReconfigureAgentType.Match) {
      this.dom.$modulesRequirementsBoxInfo.innerHTML = translateFn(
        'Target endpoints will have the exact same modules you have selected, if supported. Other installed modules will be removed.'
      );
      this.dom.$modulesRequirementsBoxInfo.innerHTML += '\n';
    }

    this.dom.$modulesRequirementsBoxInfo.innerHTML += translateFn(
      'Adding or removing modules requires these minimum agent versions: 6.6 (Windows), 6.2 (Linux), and 4.7 (macOS). For more information, check the Installation Requirements in the {installationGuide}.',
      {
        installationGuide:
          `<gz-link class="security-software-link-info" color="${LinkColor.SECONDARY}" href="${this.getModulesRequirementsLink()}">${translateFn('Installation Guide')}</gz-link>`,
      }
    );
  }

  private getModulesRequirementsLink(): string {
    if (!this.dependencies.isCloud()) {
      return 'https://www.bitdefender.com/business/support/en/77212-87490-endpoint-protection.html';
    }

    if (this.dependencies.isPartner()) {
      return 'https://www.bitdefender.com/business/support/en/77211-87490-endpoint-protection.html';
    }

    return 'https://www.bitdefender.com/business/support/en/77209-87490-endpoint-protection.html';
  }

  public setLoadingState(): void {
    this.resetTemplateState(this.dom.$wrapper);
    this.setTemplateState(this.dom.$wrapper, TemplateState.LOADING);
  }

  public removeLoadingState(): void {
    this.resetTemplateState(this.dom.$wrapper);
  }

  buildErrorView(error: Error): void {
    this.resetTemplateState(this.dom.$wrapper);
    this.setTemplateState(this.dom.$wrapper, TemplateState.ERROR);
  }

  public toggleVisibilityScanModesSection(isVisible: boolean): void {
    toggleVisibility(this.dom.$reconfigureScanModesForm, isVisible);
  }

  private initModulesSection(): void {
    const modulesForm: PackageModulesFormWebComponent = this.dom.$reconfigureAgentModulesForm;
    for (const moduleField of Object.values(modulesForm.controller.view.modulesFields)) {
      moduleField.$uiCheckbox.Validators = [
        CustomizableValidator(modulesForm.checkIfAtLeastOnModuleIsChecked.bind(modulesForm)),
      ];
    }

    modulesForm.controller.view.dom.$mailServersCheckbox.Validators = [
      CustomizableValidator(modulesForm.checkIfAtLeastOnModuleIsChecked.bind(modulesForm)),
    ];
    modulesForm.controller.view.dom.$updateServerCheckbox.Validators = [
      CustomizableValidator(modulesForm.checkIfAtLeastOnModuleIsChecked.bind(modulesForm)),
    ];
    modulesForm.controller.view.dom.$patchManagementServerCheckbox.Validators = [
      CustomizableValidator(modulesForm.checkIfAtLeastOnModuleIsChecked.bind(modulesForm)),
    ];
  }

  private initScanModeSection(availableFeatures: Feature[]): void {
    this.dom.$reconfigureScanModesForm.controller.view.formGroup.setDisabled(false);
    this.dom.$reconfigureScanModesForm.setScanModeAuto(availableFeatures);
  }

  public toggleVisibilityScanModesSectionBasedOnProductType(productType: MonthlyProductType): void {
    if (productType === MonthlyProductType.BitdefenderEdr) {
      this.toggleVisibilityScanModesSection(false);
      this.dom.$reconfigureScanModesForm.setFormFieldAutoForEdr();
    } else {
      this.toggleVisibilityScanModesSection(true);
    }
  }
}
